/**
 * Suggest page
 *
 * Form for users to submit image suggestions
 */

import { useState, type FormEvent, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Send, AlertCircle, CheckCircle, ArrowLeft, ImagePlus } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { submitSuggestion, ApiError } from '../lib/api';
import { getFingerprint } from '../lib/fingerprint';
import { CATEGORIES } from '@doyoulikethis/shared';

type SubmitterGuess = 'ai' | 'human' | 'unsure';

export function Suggest() {
  const { t } = useTranslation();

  // Form state
  const [imageUrl, setImageUrl] = useState('');
  const [sourceContext, setSourceContext] = useState('');
  const [submitterGuess, setSubmitterGuess] = useState<SubmitterGuess | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const categoryIds = Object.keys(CATEGORIES);

  const handleCategoryToggle = (categoryId: string) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((c) => c !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!imageUrl.trim()) {
      setError('Please enter an image URL');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const fingerprint = await getFingerprint();

      await submitSuggestion({
        imageUrl: imageUrl.trim(),
        sourceContext: sourceContext.trim() || undefined,
        submitterGuess: submitterGuess || undefined,
        suggestedCategories: selectedCategories.length > 0 ? selectedCategories : undefined,
        fingerprint,
      });

      setSuccess(true);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'rate_limited') {
          setError(t('suggest.rate_limited', 'You have already submitted a suggestion today. Please try again tomorrow.'));
        } else if (err.code === 'duplicate_url') {
          setError(t('suggest.duplicate', 'This image has already been suggested.'));
        } else {
          setError(err.message);
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setImageUrl('');
    setSourceContext('');
    setSubmitterGuess(null);
    setSelectedCategories([]);
    setSuccess(false);
    setError(null);
  };

  // Success state
  if (success) {
    return (
      <div className="container-app flex min-h-[60vh] items-center justify-center py-12">
        <Card variant="bordered" className="max-w-md text-center">
          <CardContent>
            <CheckCircle className="mx-auto h-12 w-12 text-vote-yes" aria-hidden="true" />
            <h2 className="mt-4 text-xl font-semibold">
              {t('suggest.success_title', 'Thank You!')}
            </h2>
            <p className="mt-2 text-gray-900 dark:text-gray-200">
              {t('suggest.success_message', 'Your suggestion has been submitted for review. We appreciate your contribution!')}
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <Button onClick={handleReset}>
                {t('suggest.submit_another', 'Submit Another')}
              </Button>
              <Link to="/vote">
                <Button variant="secondary" className="w-full">
                  {t('landing.start_voting', 'Start Voting')}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container-app py-8 sm:py-12">
      <div className="mx-auto max-w-xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <ImagePlus className="mx-auto h-12 w-12 text-accent" aria-hidden="true" />
          <h1 className="mt-4 text-2xl font-bold sm:text-3xl">
            {t('suggest.title')}
          </h1>
          <p className="mt-2 text-gray-900 dark:text-gray-200">
            {t('suggest.description')}
          </p>
        </div>

        {/* Form */}
        <Card variant="bordered">
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Image URL */}
              <div>
                <label htmlFor="imageUrl" className="block text-sm font-medium text-gray-900 dark:text-white">
                  {t('suggest.image_url')} <span className="text-red-500">*</span>
                </label>
                <Input
                  id="imageUrl"
                  type="url"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  required
                  className="mt-1"
                />
              </div>

              {/* Source Context */}
              <div>
                <label htmlFor="sourceContext" className="block text-sm font-medium text-gray-900 dark:text-white">
                  {t('suggest.source_context')}
                </label>
                <textarea
                  id="sourceContext"
                  value={sourceContext}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setSourceContext(e.target.value)}
                  placeholder={t('suggest.source_placeholder')}
                  rows={3}
                  maxLength={1000}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-900 placeholder:text-gray-500 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-400"
                />
              </div>

              {/* AI Guess */}
              <fieldset>
                <legend className="block text-sm font-medium text-gray-900 dark:text-white">
                  {t('suggest.ai_guess')}
                </legend>
                <div className="mt-2 space-y-2" role="radiogroup" aria-label={t('suggest.ai_guess')}>
                  <label className="flex cursor-pointer items-center gap-2 rounded-lg p-2 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800">
                    <input
                      type="radio"
                      name="aiGuess"
                      checked={submitterGuess === 'ai'}
                      onChange={() => setSubmitterGuess('ai')}
                      className="h-4 w-4 text-accent focus:ring-accent focus:ring-offset-2"
                    />
                    <span className="text-sm text-gray-900 dark:text-white">{t('suggest.ai_yes')}</span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 rounded-lg p-2 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800">
                    <input
                      type="radio"
                      name="aiGuess"
                      checked={submitterGuess === 'human'}
                      onChange={() => setSubmitterGuess('human')}
                      className="h-4 w-4 text-accent focus:ring-accent focus:ring-offset-2"
                    />
                    <span className="text-sm text-gray-900 dark:text-white">{t('suggest.ai_no')}</span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 rounded-lg p-2 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800">
                    <input
                      type="radio"
                      name="aiGuess"
                      checked={submitterGuess === 'unsure'}
                      onChange={() => setSubmitterGuess('unsure')}
                      className="h-4 w-4 text-accent focus:ring-accent focus:ring-offset-2"
                    />
                    <span className="text-sm text-gray-900 dark:text-white">{t('suggest.ai_unsure')}</span>
                  </label>
                </div>
              </fieldset>

              {/* Categories */}
              <fieldset>
                <legend className="block text-sm font-medium text-gray-900 dark:text-white">
                  {t('suggest.categories')}
                </legend>
                <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label={t('suggest.categories')}>
                  {categoryIds.map((categoryId) => (
                    <button
                      key={categoryId}
                      type="button"
                      onClick={() => handleCategoryToggle(categoryId)}
                      aria-pressed={selectedCategories.includes(categoryId)}
                      className={`rounded-full px-3 py-1.5 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 ${
                        selectedCategories.includes(categoryId)
                          ? 'bg-accent text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {t(`categories.${categoryId}`)}
                    </button>
                  ))}
                </div>
              </fieldset>

              {/* Warning */}
              <p className="text-xs text-gray-900 dark:text-gray-200">
                {t('suggest.warning')}
              </p>

              {/* Error */}
              {error && (
                <div
                  role="alert"
                  aria-live="assertive"
                  className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400"
                >
                  <AlertCircle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                  <span>{error}</span>
                </div>
              )}

              {/* Submit */}
              <Button
                type="submit"
                disabled={isSubmitting || !imageUrl.trim()}
                loading={isSubmitting}
                className="w-full"
                aria-describedby={error ? 'submit-error' : undefined}
              >
                <Send className="h-4 w-4" aria-hidden="true" />
                {isSubmitting ? t('common.loading') : t('suggest.submit')}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Back link */}
        <div className="mt-6 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-gray-900 hover:text-accent dark:text-gray-200"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {t('common.back')}
          </Link>
        </div>
      </div>
    </div>
  );
}
